/**
 * Admin-only widget on the Dashboard: pending dispensary assignment
 * requests submitted by sales reps. Approving here assigns the
 * dispensary; rejecting just dismisses with an optional reason.
 *
 * If the dispensary is currently owned by another rep, that's surfaced
 * loudly so the admin doesn't accidentally reassign without thinking.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Inbox,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  Clock,
  ArrowRight,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  fetchPendingAssignmentRequests,
  approveAssignmentRequest,
  rejectAssignmentRequest,
} from "../lib/api";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export function PendingAssignmentRequestsCard() {
  const [rows, setRows] = useState(null);

  const load = async () => {
    try {
      const r = await fetchPendingAssignmentRequests("pending");
      setRows(r);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Could not load pending assignment requests", err);
      setRows([]);
    }
  };
  useEffect(() => {
    load();
  }, []);

  if (rows && rows.length === 0) return null; // hide when there's nothing to do

  return (
    <div
      data-testid="pending-assignment-requests"
      className="panel p-4 border border-sky-500/30 bg-sky-500/5"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Inbox className="w-4 h-4 text-sky-300" />
          <h3 className="text-sm font-semibold text-sky-100">
            {rows === null
              ? "Loading…"
              : `${rows.length} assignment request${rows.length === 1 ? "" : "s"} from sales`}
          </h3>
        </div>
      </div>
      {rows === null ? (
        <Loader2 className="w-4 h-4 animate-spin text-sky-400" />
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((r) => (
            <RequestRow key={r.id} req={r} onChange={load} />
          ))}
        </ul>
      )}
    </div>
  );
}

function RequestRow({ req, onChange }) {
  const [busy, setBusy] = useState(false);
  const hasConflict = !!req.current_assignee_label;

  const approve = async () => {
    if (
      hasConflict &&
      !window.confirm(
        `This dispensary is currently assigned to ${req.current_assignee_label}. Approving will REASSIGN it to ${req.requested_by_email}. Continue?`
      )
    )
      return;
    setBusy(true);
    try {
      await approveAssignmentRequest(req.id);
      toast.success(`Assigned ${req.dispensary_name} → ${req.requested_by_email}`);
      onChange();
    } catch (err) {
      toast.error("Could not approve", {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusy(false);
    }
  };
  const reject = async () => {
    const reason = window.prompt(
      `Reject this request? (optional reason — sales rep will see this)`,
      ""
    );
    if (reason === null) return;
    setBusy(true);
    try {
      await rejectAssignmentRequest(req.id, reason || undefined);
      toast.success(`Request rejected`);
      onChange();
    } catch (err) {
      toast.error("Could not reject", {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      data-testid={`assignment-req-${req.id}`}
      className={`rounded border px-3 py-2.5 flex flex-col gap-2 ${
        hasConflict
          ? "border-rose-500/40 bg-rose-500/5"
          : "border-zinc-800 bg-zinc-950/60"
      }`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/dispensaries/${req.dispensary_id}`}
              className="text-sm font-medium text-zinc-50 hover:text-emerald-300 truncate"
            >
              {req.dispensary_name || "(unknown)"}
            </Link>
            <span className="text-[10px] font-mono text-zinc-500">
              {fmtDate(req.created_at)}
            </span>
            <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-900 border border-zinc-800 text-zinc-400 inline-flex items-center gap-1">
              <Clock className="w-2.5 h-2.5" />
              Pending
            </span>
          </div>
          <div className="text-xs text-zinc-400 mt-0.5 truncate">
            {req.dispensary_address}
            {req.dispensary_city ? `, ${req.dispensary_city}` : ""}
          </div>
          <div className="text-xs text-zinc-300 mt-1 inline-flex items-center gap-1 flex-wrap">
            <span className="text-zinc-500">Requested by</span>
            <span className="font-medium">{req.requested_by_name || req.requested_by_email}</span>
            <ArrowRight className="w-3 h-3 text-zinc-600" />
            <span className="text-zinc-500">to own</span>
          </div>
          {hasConflict && (
            <div
              data-testid={`assignment-conflict-${req.id}`}
              className="text-[11px] text-rose-200 bg-rose-500/15 border border-rose-500/30 rounded px-2 py-1 mt-1.5 inline-flex items-center gap-1.5"
            >
              <AlertTriangle className="w-3 h-3 shrink-0" />
              <span>
                <strong className="font-mono">Conflict:</strong> currently
                owned by <strong>{req.current_assignee_label}</strong> —
                approving will reassign.
              </span>
            </div>
          )}
          {req.note && (
            <div className="text-xs text-zinc-400 italic mt-1.5">
              "{req.note}"
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            data-testid={`approve-req-${req.id}`}
            size="sm"
            onClick={approve}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 h-8"
          >
            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
            Approve
          </Button>
          <Button
            data-testid={`reject-req-${req.id}`}
            size="sm"
            onClick={reject}
            disabled={busy}
            variant="outline"
            className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 h-8"
          >
            <XCircle className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </li>
  );
}
